import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, DollarSign, TrendingUp, ShoppingBag, Star, BarChart3, Clock, Percent } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { getCreatorEarnings } from "../lib/api";

function StatCard({ icon: Icon, label, value, color = "#E5FF00", sub }) {
  return (
    <div className="bg-[#111620] border border-[#273142] p-5 rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} style={{ color }} />
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#475569]">{label}</span>
      </div>
      <p className="text-2xl font-semibold" style={{ color }}>{value}</p>
      {sub && <p className="font-mono text-[10px] text-[#475569] mt-1">{sub}</p>}
    </div>
  );
}

function RatingStars({ rating, size = 12 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={size} className={i <= Math.round(rating) ? "text-[#E5FF00] fill-[#E5FF00]" : "text-[#273142]"} />
      ))}
    </div>
  );
}

export default function EarningsPage({ user }) {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getCreatorEarnings()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <div data-testid="earnings-page" className="h-screen bg-[#080B10] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#273142] px-8 py-4 flex items-center gap-4">
        <button data-testid="earnings-back-btn" onClick={() => navigate("/agents")}
          className="text-[#475569] hover:text-[#E5FF00] transition-colors flex items-center gap-1.5">
          <ArrowLeft size={16} /><span className="font-mono text-xs uppercase tracking-wider">Back</span>
        </button>
        <div className="flex items-center gap-2 ml-2">
          <BarChart3 size={18} className="text-[#E5FF00]" />
          <h1 className="text-lg font-semibold tracking-tight">CREATOR EARNINGS</h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <SignedOut><SignInButton mode="modal"><button className="bg-[#E5FF00] text-[#080B10] font-semibold text-[10px] px-4 py-2 rounded-lg hover:bg-[#D4EC00]">SIGN IN</button></SignInButton></SignedOut>
          <SignedIn><div className="flex items-center gap-2"><span className="text-xs text-[#94A3B8]">{clerkUser?.firstName}</span><UserButton appearance={{ elements: { userButtonAvatarBox: "w-7 h-7" } }} /></div></SignedIn>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="px-8 py-6 max-w-6xl mx-auto">
          {!user && (
            <div className="text-center py-20">
              <DollarSign size={32} className="text-[#273142] mx-auto mb-3" />
              <p className="text-[#475569] text-sm font-mono uppercase tracking-widest">Sign in to view earnings</p>
            </div>
          )}

          {loading && user && (
            <div className="text-center py-20">
              <p className="text-[#475569] text-sm font-mono">Loading earnings data...</p>
            </div>
          )}

          {user && !loading && data && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={DollarSign} label="Gross Earnings" value={`$${data.total_earnings.toFixed(2)}`} />
                <StatCard icon={Percent} label="Platform Fees (10%)" value={`$${data.platform_fees.toFixed(2)}`} color="#FF3366" />
                <StatCard icon={TrendingUp} label="Net Earnings" value={`$${data.net_earnings.toFixed(2)}`} color="#10B981" />
                <StatCard icon={ShoppingBag} label="Total Sales" value={data.total_sales} color="#3B82F6" />
              </div>

              {/* Capsule Performance */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag size={14} className="text-[#E5FF00]" />
                  <h2 className="text-sm font-semibold">Capsule Performance</h2>
                  <span className="font-mono text-[10px] text-[#475569]">{data.capsules.length} capsules</span>
                </div>

                {data.capsules.length === 0 ? (
                  <div className="bg-[#111620] border border-[#273142] rounded-xl p-8 text-center">
                    <p className="text-[#475569] text-sm font-mono">No capsules published yet</p>
                    <p className="text-[#475569] text-xs mt-1">Publish a chat to the marketplace to start earning</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.capsules.map(cap => (
                      <div key={cap.id} data-testid={`earnings-capsule-${cap.id}`}
                        className="bg-[#111620] border border-[#273142] rounded-xl p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-semibold truncate">{cap.title}</h3>
                            <span className="font-mono text-[10px] px-1.5 py-0.5 border rounded-full text-[#94A3B8] border-[#273142] uppercase">{cap.category}</span>
                          </div>
                          <div className="flex items-center gap-4 text-[10px] font-mono text-[#475569]">
                            <span>{cap.usage_count} uses</span>
                            <span>{cap.sales_count} sales</span>
                            <span>${cap.preview_price.toFixed(2)}/query</span>
                            <span>${cap.pass_price.toFixed(2)}/pass</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            {cap.review_count > 0 && (
                              <div className="flex items-center gap-1 mb-0.5 justify-end">
                                <RatingStars rating={cap.avg_rating} size={10} />
                                <span className="font-mono text-[10px] text-[#94A3B8]">{cap.avg_rating}</span>
                              </div>
                            )}
                            <span className="font-mono text-[10px] text-[#475569]">{cap.review_count} reviews</span>
                          </div>
                          <div className="text-right min-w-[80px]">
                            <p className="text-lg font-semibold text-[#10B981]">${cap.revenue.toFixed(2)}</p>
                            <span className="font-mono text-[10px] text-[#475569]">revenue</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Monthly Earnings */}
              {data.monthly_earnings.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={14} className="text-[#10B981]" />
                    <h2 className="text-sm font-semibold">Monthly Earnings</h2>
                  </div>
                  <div className="bg-[#111620] border border-[#273142] rounded-xl p-4">
                    <div className="flex items-end gap-2 h-32">
                      {data.monthly_earnings.slice().reverse().map(m => {
                        const max = Math.max(...data.monthly_earnings.map(x => x.earnings), 1);
                        const h = Math.max((m.earnings / max) * 100, 4);
                        return (
                          <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                            <span className="font-mono text-[10px] text-[#10B981]">${m.earnings.toFixed(0)}</span>
                            <div className="w-full bg-[#10B981]/20 rounded-t" style={{ height: `${h}%` }}>
                              <div className="w-full h-full bg-[#10B981]/60 rounded-t" />
                            </div>
                            <span className="font-mono text-[10px] text-[#475569]">{m.month.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Transactions */}
              {data.recent_transactions.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={14} className="text-[#3B82F6]" />
                    <h2 className="text-sm font-semibold">Recent Transactions</h2>
                  </div>
                  <div className="bg-[#111620] border border-[#273142] rounded-xl overflow-hidden">
                    <div className="grid grid-cols-5 gap-4 px-4 py-2 border-b border-[#273142] text-[10px] font-mono text-[#475569] uppercase tracking-wider">
                      <span>Capsule</span><span>Gross</span><span>Fee</span><span>Net</span><span>Date</span>
                    </div>
                    {data.recent_transactions.map(tx => (
                      <div key={tx.id} data-testid={`tx-${tx.id}`}
                        className="grid grid-cols-5 gap-4 px-4 py-3 border-b border-[#273142]/50 text-xs">
                        <span className="truncate">{tx.capsule_title}</span>
                        <span className="font-mono text-[#94A3B8]">${tx.gross_amount.toFixed(2)}</span>
                        <span className="font-mono text-[#FF3366]">-${tx.platform_fee.toFixed(2)}</span>
                        <span className="font-mono text-[#10B981]">${tx.net_amount.toFixed(2)}</span>
                        <span className="font-mono text-[#475569]">{tx.created_at.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
